import { useSelector } from 'react-redux';
import { format } from 'date-fns';

import { useAssets } from '@labradorsports/assets';
import { PlayTagDisplay, QuillWrapper } from '@labradorsports/components';
import { ProfileFlags } from '@labradorsports/constants';
import { noPropagation, useSite } from '@labradorsports/utils';

import { getAssetUrl, useDispatcher, useNavigateWithQuery } from '../../shared/index.js';
import {
    communityActions,
    useAddCommunityPlayLikeMutation,
    useCopyCommunityPlayMutation,
    useMyPlaybookQuery,
} from '../../store/index.js';
import { RootState, Selectors } from '../../store/state.js';

const getPrettyViewCount = (viewCount: number) => {
    if (viewCount < 1000) {
        return `${viewCount}`;
    }

    if (viewCount < 1000000) {
        const thousands = Math.round(viewCount / 100) / 10;

        return `${thousands}K`;
    }

    // If we get to billions, it will probably be someone else working on this code
    const millions = Math.round(viewCount / 100000) / 10;

    return `${millions}M`;
};

interface Props {
    play: any;
}

const CommunityPlayDetails: FC<Props> = ({ play }) => {
    const navigate = useNavigateWithQuery();
    const { CopyIcon, LikeIcon, LikeIconFilled } = useAssets();
    const { Config, PlayConfig } = useSite();
    const [copyCommunityPlay] = useCopyCommunityPlayMutation();
    const [addCommunityPlayLike] = useAddCommunityPlayLikeMutation();
    const { data: myPlaybook } = useMyPlaybookQuery();
    const showSignupPrompt = useDispatcher(communityActions.ShowSignupPrompt);
    const uid = useSelector(Selectors.uid);
    const hasProfile = useSelector((state: RootState) =>
        Selectors.userHasFlag(state, ProfileFlags.HAS_COMMUNITY_PROFILE)
    );
    const { PlayTypes, FieldDescriptor } = PlayConfig;

    if (!play) {
        return null;
    }

    const viewerCount: number = Object.values(play.viewers ?? {}).reduce<number>((accumulator, viewer: any) => {
        return accumulator + viewer.viewCount;
    }, 0);

    const profileClick = noPropagation((evt) => {
        evt.preventDefault();
        navigate('/community/profile', {
            communityProfileId: play.sharedBy,
        });
    });

    const copyPlayClick = noPropagation(() => {
        if (uid && hasProfile) {
            copyCommunityPlay({ id: play.id, playbookId: myPlaybook.id });
        } else {
            showSignupPrompt(true);
        }
    });

    const likePlayClick = noPropagation(() => {
        if (uid) {
            addCommunityPlayLike({ id: play.id });
        } else {
            showSignupPrompt(true);
        }
    });

    const PlayTypeDisplay = {
        [PlayTypes.HALF_FIELD]: `Half-${FieldDescriptor}`,
        [PlayTypes.FULL_FIELD]: `Full-${FieldDescriptor}`,
        [PlayTypes.DRILL]: 'Drill',
    };

    const likesCount = Object.values(play.likes ?? {}).filter((v) => v).length;

    const preventDefault = (evt) => {
        evt.preventDefault();
    };

    return (
        <div className="community-play-details">
            <div className="play-details">
                <a
                    className="play-name"
                    href={`/community/play?p=${play.id}`}
                    title={play.name}
                    onClick={preventDefault}
                >
                    <em>{play.name}</em>
                </a>
                <button className="copy-button" onClick={likePlayClick}>
                    <img src={getAssetUrl(play.likes?.[uid] ? LikeIconFilled : LikeIcon)} alt="Like icon" />
                    <span className="small d-none d-sm-inline">Like{play.likes?.[uid] ? 'd' : ''}</span>
                </button>
                <button className="copy-button" onClick={copyPlayClick}>
                    <img src={getAssetUrl(CopyIcon)} alt="Copy icon" />
                    <span className="small d-none d-sm-inline">Copy</span>
                </button>
            </div>
            <div className="small text-muted">
                <span>{Config.SportsNames[play.sport]}</span>
                <span className="divider">|</span>
                <span>{PlayTypeDisplay[play.type]}</span>
            </div>
            <div className="description small">
                <QuillWrapper defaultValue={play.description} readOnly />
            </div>
            <div className="play-tags">
                <PlayTagDisplay tags={play.tags} />
            </div>
            <div className="text-muted small engagements">
                <span>{getPrettyViewCount(viewerCount)} Views</span>
                <span className="divider">|</span>
                <span>{likesCount} Likes</span>
                <span className="divider">|</span>
                <span>{play.commentsCount ?? 0} Comments</span>
            </div>
            <div className="sharing-details small">
                <a href={`/community/profile?pr=${play.sharedBy}`} className="button-link" onClick={profileClick}>
                    {play.sharedBy}
                </a>
                <span className="divider">|</span>
                <span className="text-muted">{format(play.sharedOn, 'MMM d, yyyy')}</span>
            </div>
        </div>
    );
};

export default CommunityPlayDetails;
